import React from 'react';
import { Typography, Box, Checkbox, FormControlLabel } from '@mui/material';
import { TextField } from 'ui/components/TextField/TextField';
import { LabelElement, TextElementProps } from '../../../types';
import { Autocomplete } from 'ui/components/Autocomplete/Autocomplete';
import CompactColorPicker from 'ui/components/CompactColorPicker/CompactColorPicker';

interface TextPropertiesProps {
  selectedElement: LabelElement | null;
  updateElementProperty: (key: string, value: any) => void;
  handleKeyDown: (e: React.KeyboardEvent) => void;
  textProps: TextElementProps;
  setTextProps: React.Dispatch<React.SetStateAction<TextElementProps>>;
}

const TextProperties = ({
  selectedElement,
  updateElementProperty,
  handleKeyDown,
  textProps,
  setTextProps,
}: TextPropertiesProps) => {
  const isTransparent = textProps.backgroundColor === 'transparent';

  const handleTransparentToggle = (checked: boolean) => {
    const newColor = checked ? 'transparent' : '#ffffff'; // Default to white when unchecked
    setTextProps((prev) => ({ ...prev, backgroundColor: newColor }));
    updateElementProperty('backgroundColor', newColor);
  };

  return (
    <>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Text Properties
      </Typography>
      {!selectedElement?.dynamicField && (
        <TextField
          className="redesign"
          variant="standard"
          label="Text Content"
          size="small"
          fullWidth
          value={textProps.text}
          onChange={(e) => {
            const newText = e.target.value;
            setTextProps((prev) => ({ ...prev, text: newText }));
            updateElementProperty('text', newText);
          }}
          onKeyDown={handleKeyDown}
          sx={{ mb: 2 }}
        />
      )}
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <TextField
          className="redesign"
          variant="standard"
          label="Font Size"
          size="small"
          type="number"
          value={textProps.fontSize}
          onChange={(e) => {
            const value = e.target.value;
            setTextProps((prev) => ({
              ...prev,
              fontSize: value === '' ? undefined : Number(value),
            }));
            if (value !== '' && !isNaN(Number(value))) {
              updateElementProperty('fontSize', Number(value));
            }
          }}
          onKeyDown={handleKeyDown}
          sx={{ width: '50%' }}
        />
        <Box sx={{ width: '50%' }}>
          <Autocomplete
            label="Font"
            value={textProps.fontFamily}
            onChange={(event, newValue) => {
              if (newValue) {
                setTextProps((prev) => ({ ...prev, fontFamily: newValue }));
                updateElementProperty('fontFamily', newValue);
              }
            }}
            options={[
              'Arial',
              'Times New Roman',
              'Courier New',
              'Georgia',
              'Verdana',
            ]}
            getOptionLabel={(option) => option}
          />
        </Box>
      </Box>
      <CompactColorPicker
        label="Text Color"
        color={textProps.fill || '#000000'}
        onChange={(color) => {
          setTextProps((prev) => ({ ...prev, fill: color }));
          updateElementProperty('fill', color);
        }}
      />
      <Box sx={{ mt: 1 }}>
        <CompactColorPicker
          label="Background Color"
          color={
            isTransparent ? '#ffffff' : textProps.backgroundColor || '#ffffff'
          }
          onChange={(color) => {
            if (!isTransparent) {
              setTextProps((prev) => ({ ...prev, backgroundColor: color }));
              updateElementProperty('backgroundColor', color);
            }
          }}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={isTransparent}
              onChange={(e) => handleTransparentToggle(e.target.checked)}
            />
          }
          label="Transparent"
          sx={{ ml: 'auto' }}
        />
      </Box>
    </>
  );
};

export default TextProperties;
